/**
 * Dashboard Usage and limits card (#1616): how close each free tier or cap is. The card shows every limit as a small
 * column (#1635); "All limits" lists them with their numbers and holds the Gemini cap (#1623). The numbers come
 * from the getUsageLimits callable, which reads Cloud Monitoring, the Cloud Billing export, Cloudinary, Upstash, GitHub and Resend;
 * this file asks for them, draws them, and saves the one limit an admin enters, the amount of Agent Platform's spend cap
 * budget for Gemini (#2363), to `globals/usage_limits`.
 */

import { siCloudinary, siFirebase, siGithub, siGooglegemini, siResend, siUpstash } from "simple-icons";
import { BUTTON, FIELD } from "./ui/classes.js";

// Bars turn yellow (amber) from 50 % and red from 75 % of their limit (#1635).
const WARN_AT = 0.5;
const DANGER_AT = 0.75;

// Tailwind class lists; whole strings, so Tailwind finds them.
const BAR_TONE = {
  ok: "h-full rounded-full bg-emerald-500 dark:bg-emerald-400",
  warn: "h-full rounded-full bg-amber-500 dark:bg-amber-400",
  danger: "h-full rounded-full bg-red-600 dark:bg-red-500",
  none: "h-full rounded-full bg-gray-300 dark:bg-white/20"
};
const PCT_TONE = {
  ok: "font-semibold text-gray-900 dark:text-white",
  warn: "font-semibold text-amber-700 dark:text-amber-400",
  danger: "font-semibold text-red-700 dark:text-red-400",
  none: "font-semibold text-gray-500 dark:text-gray-400"
};

const panel = {
  deps: null,
  status: "idle", // idle | loading | ok | error
  data: null,
  error: null,
  savingCap: false
};

let listenersBound = false;

/**
 * Asks getUsageLimits for the numbers and draws them. Call once the signed-in user is known to be an admin;
 * `refresh` asks the function to skip its cache. `deps`: `{ functions, httpsCallable, db, doc, setDoc,
 * serverTimestamp, auth, showToast }`.
 */
export async function loadUsageLimits(deps = panel.deps, { refresh = false } = {}) {
  if (!deps?.functions || !deps?.httpsCallable) return;
  panel.deps = deps;
  bindListeners();
  if (panel.status === "loading") return;
  panel.status = "loading";
  panel.error = null;
  renderUsagePanel();
  try {
    const result = await deps.httpsCallable(deps.functions, "getUsageLimits")({ refresh });
    panel.data = result.data;
    panel.status = "ok";
  } catch (err) {
    console.error("❌ getUsageLimits failed:", err);
    panel.error = err?.message || String(err);
    panel.status = "error";
  }
  renderUsagePanel();
}

/**
 * Draws the current state into the dashboard template, if it is mounted: the status line and the limits closest to
 * their cap in the card (#1623), every limit and the cap form under "All limits".
 */
export function renderUsagePanel() {
  const body = document.getElementById("dashUsage");
  const all = document.getElementById("dashUsageAll");
  const count = document.getElementById("dashUsageCount");
  const meta = document.getElementById("dashUsageMeta");
  const button = document.getElementById("dashUsageRefresh");
  if (!body) return;
  if (button) button.disabled = panel.status === "loading" || !panel.deps;
  if (meta) meta.textContent = metaText(panel);
  const now = Date.now();
  const rows = allRows(panel.data);
  if (count) count.textContent = rows.length ? ` (${rows.length})` : "";

  if (panel.status === "error") {
    body.innerHTML = `<p class="px-4 py-8 text-center text-sm text-red-700 sm:px-6 dark:text-red-400">Could not load usage: ${escapeHtml(panel.error)}</p>`;
  } else if (panel.data) {
    body.innerHTML = usageSummaryHtml(panel.data, { now });
  } else {
    body.innerHTML = `<p class="px-4 py-8 text-center text-sm text-gray-500 sm:px-6 dark:text-gray-400">Loading usage…</p>`;
  }
  if (all) {
    all.innerHTML = panel.data && panel.status !== "error"
      ? usageHtml(panel.data, { now, savingCap: panel.savingCap })
      : `<p class="px-4 py-8 text-center text-sm text-gray-500 sm:px-6 dark:text-gray-400">${panel.status === "error" ? "Nothing to show until usage loads." : "Loading…"}</p>`;
  }
}

function allRows(data) {
  return Array.isArray(data?.groups) ? data.groups.flatMap((g) => (g.rows || []).map((r) => ({ ...r, group: g.title }))) : [];
}

// The chart's short names (#1635); the full label is in the tooltip and under "All limits".
const SHORT_LABEL = {
  firestore_reads: "Reads",
  firestore_writes: "Writes",
  firestore_deletes: "Deletes",
  firestore_stored: "Stored",
  function_invocations: "Functions",
  storage_stored: "Storage",
  storage_downloaded: "Downloads",
  hosting_stored: "Hosting",
  hosting_transfer: "Transfer",
  gemini_spend: "Spend",
  cloudinary_credits: "Credits",
  upstash_commands: "Commands",
  upstash_storage: "Storage",
  upstash_bandwidth: "Bandwidth",
  gemini_estimate: "Est. spend",
  gemini_tokens: "Tokens",
  gemini_tokens_today: "Tokens",
  help_suggestions: "Help",
  github_free_minutes: "Free min",
  github_storage: "Storage",
  github_spend: "Spend",
  github_projected: "Projected",
  resend_emails_daily: "Today",
  resend_emails_monthly: "Month",
  resend_contacts: "Contacts"
};

// Readings with no limit to measure against (#1668): drawn as a number in a box, not a bar, and not counted as limits.
const READINGS = new Set(["gemini_estimate", "gemini_tokens", "gemini_tokens_today", "help_suggestions"]);
const READING_BOX = "mt-1 flex h-28 w-14 items-center justify-center rounded-sm bg-gray-50 outline-1 -outline-offset-1 outline-gray-200 dark:bg-white/5 dark:outline-white/10";

/** A value short enough for a column: "$73", "25.5M", "3.4 GB". */
export function compactValue(row) {
  if (typeof row.used !== "number") return "–";
  if (row.unit === "bytes") return formatValue(row.used, row);
  if (row.unit === "money") {
    try {
      return new Intl.NumberFormat("en-US", { style: "currency", currency: row.currency || "USD", notation: "compact", minimumFractionDigits: row.used < 10 ? 2 : 0, maximumFractionDigits: row.used < 10 ? 2 : 0 }).format(row.used);
    } catch (err) {
      return String(Math.round(row.used));
    }
  }
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(row.used);
}
const SHORT_GROUP = { firebase: "Firebase", gemini: "Gemini", cloudinary: "Cloudinary", upstash: "Upstash", github: "GitHub", resend: "Resend" };

// Each provider's logo next to its section (#1641), from simple-icons (CC0), bundled so nothing loads from outside.
// A logo keeps its brand colour (an SVG fill, the one colour here that isn't a Tailwind class) on a white tile, so it
// reads in both themes.
const PROVIDER_ICON = { firebase: siFirebase, gemini: siGooglegemini, cloudinary: siCloudinary, upstash: siUpstash, github: siGithub, resend: siResend };

/** The provider's logo on a small white tile, or nothing for a group without one. */
export function providerIconHtml(groupKey) {
  const icon = PROVIDER_ICON[groupKey];
  if (!icon) return "";
  return `<span class="inline-flex size-8 flex-none items-center justify-center rounded-md bg-white shadow-xs outline-1 -outline-offset-1 outline-black/10 dark:shadow-none dark:outline-white/10" aria-hidden="true"><svg viewBox="0 0 24 24" class="size-5" fill="#${icon.hex}"><path d="${icon.path}" /></svg></span>`;
}
const SHORT_WINDOW = { today: "today", "last 24 hours": "24 h", "this month": "month", now: "now", "this plan cycle": "cycle" };

// A column's fill, anchored to the baseline with a rounded top; whole strings, so Tailwind finds them.
const COLUMN_FILL = {
  ok: "absolute inset-x-0 bottom-0 rounded-t bg-emerald-500 dark:bg-emerald-400",
  warn: "absolute inset-x-0 bottom-0 rounded-t bg-amber-500 dark:bg-amber-400",
  danger: "absolute inset-x-0 bottom-0 rounded-t bg-red-600 dark:bg-red-500"
};
const TRACK = "relative mt-1 h-28 w-9 overflow-hidden rounded-sm bg-gray-100 dark:bg-white/10";
const TRACK_EMPTY = "relative mt-1 h-28 w-9 rounded-sm outline-1 -outline-offset-1 outline-dashed outline-gray-300 dark:outline-white/20";
// The 50 % and 75 % marks across each column, where the colour changes.
const MARKS = `<span class="absolute inset-x-0 bottom-[50%] border-t border-dashed border-amber-500/60"></span><span class="absolute inset-x-0 bottom-[75%] border-t border-dashed border-red-600/60"></span>`;

/**
 * The words a column's tooltip and screen reader text carry: what it is, how much of what, and when it resets, then
 * what the bar means where the row says (`about`, on its own line).
 */
export function columnText(row, now) {
  const text = columnFigures(row, now);
  return row.about ? `${text}\n${row.about}` : text;
}

function columnFigures(row, now) {
  const amount = row.limit === null || row.limit === undefined
    ? formatValue(row.used, row)
    : `${formatValue(row.used, row)} of ${formatValue(row.limit, row)}`;
  const tone = toneFor(row.used, row.limit);
  const pct = tone === "none" ? "" : ` (${Math.round((row.used / row.limit) * 100)}%)`;
  const when = [row.window, row.resets_at ? `resets ${untilText(row.resets_at, now)}` : null].filter(Boolean).join(", ");
  if (row.error) return `${row.label}: ${row.error}`;
  if (typeof row.used !== "number") return `${row.label}: no number`;
  const noCap = row.limit === null || row.limit === undefined ? " · no limit set" : "";
  return `${row.label}: ${amount}${pct}${when ? ` · ${when}` : ""}${noCap}`;
}

function columnHtml(row, now) {
  const tone = toneFor(row.used, row.limit);
  const share = tone === "none" ? null : row.used / row.limit;
  const unreadable = row.error || typeof row.used !== "number";
  const reading = READINGS.has(row.key) && !unreadable;
  let top;
  if (reading) top = `<span class="text-sm">&nbsp;</span>`;
  else if (unreadable) top = `<span class="text-sm font-semibold text-gray-400 dark:text-gray-500">–</span>`;
  else if (share === null) top = `<span class="text-sm font-semibold text-gray-700 tabular-nums dark:text-gray-300">${escapeHtml(compactValue(row))}</span>`;
  else top = `<span class="text-sm tabular-nums ${PCT_TONE[tone]}">${Math.round(share * 100)}%</span>`;
  const height = share === null ? 0 : Math.min(100, Math.max(row.used > 0 ? 2 : 0, share * 100));
  let plot;
  if (reading) plot = `<div class="${READING_BOX}"><span class="text-sm font-semibold text-gray-900 tabular-nums dark:text-white">${escapeHtml(compactValue(row))}</span></div>`;
  else if (unreadable || share === null) plot = `<div class="${TRACK_EMPTY}">${MARKS}</div>`;
  else plot = `<div class="${TRACK}"><div class="${COLUMN_FILL[tone]}" style="height: ${height}%"></div>${MARKS}</div>`;
  const text = columnText(row, now);
  const windowLabel = SHORT_WINDOW[row.window] || row.window || "";
  return `
    <li class="flex w-16 flex-none flex-col items-center" data-usage-bar="${escapeHtml(row.key)}" data-tooltip="${escapeHtml(text)}">
      <span class="sr-only">${escapeHtml(text)}</span>
      <div aria-hidden="true" class="flex flex-col items-center">
        ${top}
        ${plot}
        <span class="mt-2 text-center text-xs font-medium text-gray-700 dark:text-gray-300">${escapeHtml(SHORT_LABEL[row.key] || row.label)}</span>
        <span class="text-center text-[11px]/4 text-gray-500 dark:text-gray-400">${escapeHtml(row.error ? "can't read" : (row.limit === null || row.limit === undefined) && row.key === "gemini_spend" ? "no cap" : windowLabel)}</span>
      </div>
    </li>`;
}

/**
 * The card's short form: a status line (amber, red, unreadable, and the Gemini cap when none is set) and every limit
 * as a small column of its share, grouped by provider (#1635). Exact numbers are in each column's tooltip and under
 * "All limits".
 */
export function usageSummaryHtml(data, { now = Date.now() } = {}) {
  const rows = allRows(data);
  if (!rows.length) return `<p class="px-4 py-8 text-center text-sm text-gray-500 sm:px-6 dark:text-gray-400">No usage came back.</p>`;
  const limits = rows.filter((r) => !READINGS.has(r.key));
  const tones = limits.map((r) => toneFor(r.used, r.limit));
  const red = tones.filter((t) => t === "danger").length;
  const amber = tones.filter((t) => t === "warn").length;
  const unreadable = rows.filter((r) => r.error).length;
  const noCap = limits.some((r) => r.key === "gemini_spend" && (r.limit === null || r.limit === undefined) && !r.error);

  const parts = [];
  if (red) parts.push(`<span class="font-semibold text-red-700 dark:text-red-400">${red} red</span>`);
  if (amber) parts.push(`<span class="font-semibold text-amber-700 dark:text-amber-400">${amber} yellow</span>`);
  if (!red && !amber) parts.push(`<span class="font-semibold text-gray-900 dark:text-white">all under 50%</span>`);
  if (unreadable) parts.push(`${unreadable} can't be read`);
  if (noCap) parts.push("no Gemini cap set");
  const status = `<p class="text-sm text-gray-600 dark:text-gray-300">${limits.length} limits · ${parts.join(" · ")}</p>`;

  const groups = data.groups.filter((g) => (g.rows || []).length).map((g) => `
      <section class="min-w-0 rounded-lg px-3 py-3 outline-1 -outline-offset-1 outline-gray-200 dark:outline-white/10" aria-label="${escapeHtml(g.title)}">
        <h4 class="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">${providerIconHtml(g.key)}${escapeHtml(SHORT_GROUP[g.key] || g.title)}</h4>
        <ul role="list" class="mt-3 flex flex-wrap gap-y-4">${g.rows.map((r) => columnHtml(r, now)).join("")}</ul>
      </section>`).join("");
  return `
    <div class="px-4 py-4 sm:px-6">
      ${status}
      <div class="mt-4 flex flex-wrap gap-4">${groups}</div>
    </div>`;
}

function bindListeners() {
  if (listenersBound) return;
  listenersBound = true;
  document.addEventListener("click", (e) => {
    if (e.target.closest("#dashUsageRefresh")) loadUsageLimits(panel.deps, { refresh: true });
  });
  document.addEventListener("submit", (e) => {
    const form = e.target.closest?.("#dashUsageCapForm");
    if (!form) return;
    e.preventDefault();
    saveGeminiCap(form.elements.cap.value);
  });
}

/** The cap as typed: a non-negative number, or null for an empty field (no cap). Throws on anything else. */
export function parseCap(text) {
  const trimmed = String(text ?? "").trim().replace(",", ".");
  if (trimmed === "") return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0) throw new Error("The cap must be a number, 0 or more.");
  return Math.round(value * 100) / 100;
}

async function saveGeminiCap(text) {
  const deps = panel.deps;
  if (!deps?.db || panel.savingCap) return;
  let cap;
  try {
    cap = parseCap(text);
  } catch (err) {
    deps.showToast?.(err.message, "error");
    return;
  }
  panel.savingCap = true;
  renderUsagePanel();
  try {
    // A whole replace, so clearing the field removes the cap rather than leaving the old one.
    const fields = { updated_at: deps.serverTimestamp(), updated_by: deps.auth?.currentUser?.email || null };
    if (cap !== null) fields.gemini_monthly_cap = cap;
    await deps.setDoc(deps.doc(deps.db, "globals", "usage_limits"), fields);
    // The function caches for minutes; show the saved cap now.
    const row = panel.data?.groups?.flatMap((g) => g.rows).find((r) => r.key === "gemini_spend");
    if (row) {
      row.limit = cap;
      row.note = cap === null ? "No cap set here yet: enter the amount of Agent Platform's spend cap budget (Billing → Budgets & alerts)." : null;
    }
    deps.showToast?.(cap === null ? "Gemini cap cleared." : "Gemini cap saved.", "success");
  } catch (err) {
    console.error("❌ Saving the Gemini cap failed:", err);
    deps.showToast?.(`Could not save the cap: ${err?.message || err}`, "error");
  } finally {
    panel.savingCap = false;
    renderUsagePanel();
  }
}

function metaText({ status, data }) {
  if (status === "loading" && data) return "Refreshing…";
  if (!data) return "How close each free tier and cap is.";
  return `How close each free tier and cap is, read at ${formatTime(data.fetched_at)}${data.cached ? " (cached)" : ""}. Yellow from 50 %, red from 75 %.`;
}

function formatTime(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "an unknown time";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/** "3.4 GB", or "221 MiB" when the limit is in binary units (Firestore's 1 GiB). */
export function formatBytes(bytes, binary = false) {
  if (typeof bytes !== "number") return "–";
  const base = binary ? 1024 : 1000;
  const units = binary ? ["B", "KiB", "MiB", "GiB", "TiB"] : ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let i = 0;
  while (Math.abs(value) >= base && i < units.length - 1) {
    value /= base;
    i++;
  }
  const digits = value >= 100 || i === 0 ? 0 : value >= 10 ? 1 : 2;
  return `${Number(value.toFixed(digits))} ${units[i]}`;
}

/** A row's value in its unit: a count, bytes or money. */
export function formatValue(value, row) {
  if (typeof value !== "number") return "–";
  if (row.unit === "bytes") {
    const binary = typeof row.limit === "number" && row.limit > 0 && row.limit % (1024 * 1024) === 0;
    return formatBytes(value, binary);
  }
  if (row.unit === "money") {
    try {
      return new Intl.NumberFormat("en-US", { style: "currency", currency: row.currency || "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
    } catch (err) {
      return value.toFixed(2);
    }
  }
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(value);
}

/** `ok`, `warn`, `danger`, or `none` when there is nothing to compare. */
export function toneFor(used, limit) {
  if (typeof used !== "number" || typeof limit !== "number" || limit <= 0) return "none";
  const share = used / limit;
  if (share >= DANGER_AT) return "danger";
  if (share >= WARN_AT) return "warn";
  return "ok";
}

/** "in 5 h", "in 3 days" until `iso` from `now`. */
export function untilText(iso, now) {
  const ms = Date.parse(iso) - now;
  if (!Number.isFinite(ms)) return "";
  if (ms <= 0) return "now";
  const hours = ms / 3600000;
  if (hours < 1) return `in ${Math.max(1, Math.round(ms / 60000))} min`;
  if (hours < 48) return `in ${Math.round(hours)} h`;
  return `in ${Math.round(hours / 24)} days`;
}

/** The error every row of a group shares (one cause, such as a missing permission), or null. */
export function sharedError(rows = []) {
  if (rows.length < 2 || !rows.every((r) => r.error)) return null;
  return rows.every((r) => r.error === rows[0].error) ? rows[0].error : null;
}

function rowHtml(row, now, { hideError = false } = {}) {
  const tone = toneFor(row.used, row.limit);
  const hasShare = tone !== "none";
  const pct = hasShare ? Math.round((row.used / row.limit) * 100) : null;
  const width = hasShare ? Math.min(100, Math.max(row.used > 0 ? 1 : 0, (row.used / row.limit) * 100)) : 0;
  const amount = row.limit === null || row.limit === undefined
    ? formatValue(row.used, row)
    : `${formatValue(row.used, row)} of ${formatValue(row.limit, row)}`;
  const when = [
    row.window ? capitalise(row.window) : null,
    row.resets_at ? `resets ${untilText(row.resets_at, now)}` : null,
    row.as_of ? `data as of ${formatTime(row.as_of)}` : null,
    row.note
  ].filter(Boolean).join(" · ");

  return `
    <li class="py-3" data-usage-row="${escapeHtml(row.key)}">
      <div class="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <p class="text-sm font-medium text-gray-900 dark:text-white">${escapeHtml(row.label)}</p>
        <p class="text-sm text-gray-700 tabular-nums dark:text-gray-300">${row.error && row.used === null ? "" : escapeHtml(amount)}${pct !== null ? ` <span class="${PCT_TONE[tone]}">${pct}%</span>` : ""}</p>
      </div>
      ${READINGS.has(row.key) ? "" : `<div class="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-white/10" role="presentation"><div class="${BAR_TONE[tone]}" style="width: ${width}%"></div></div>`}
      ${when ? `<p class="mt-1 text-xs text-gray-500 dark:text-gray-400">${escapeHtml(when)}</p>` : ""}
      ${row.error && !hideError ? `<p class="mt-1 text-xs break-words text-red-700 dark:text-red-400">${escapeHtml(row.error)}</p>` : ""}
    </li>`;
}

function capForm(row, savingCap) {
  const value = typeof row?.limit === "number" ? String(row.limit) : "";
  return `
    <form id="dashUsageCapForm" class="mt-1 flex flex-wrap items-end gap-2">
      <div>
        <label for="dashUsageCap" class="block text-xs font-medium text-gray-700 dark:text-gray-300">Monthly cap: Agent Platform's spend cap budget${row?.currency ? ` (${escapeHtml(row.currency)})` : ""}</label>
        <input id="dashUsageCap" name="cap" type="number" min="0" step="0.01" inputmode="decimal" value="${escapeHtml(value)}" placeholder="No cap" class="${FIELD.input} mt-1 w-32">
      </div>
      <button type="submit" class="${BUTTON.secondary}" ${savingCap ? "disabled" : ""}>${savingCap ? "Saving…" : "Save cap"}</button>
    </form>`;
}

/** The card body for a getUsageLimits result. */
export function usageHtml(data, { now = Date.now(), savingCap = false } = {}) {
  const groups = Array.isArray(data?.groups) ? data.groups : [];
  if (!groups.length) return `<p class="px-4 py-8 text-center text-sm text-gray-500 sm:px-6 dark:text-gray-400">No usage came back.</p>`;
  return `
    <div class="columns-1 gap-x-10 px-4 pt-5 sm:px-6 lg:columns-2">
      ${groups.map((g) => {
        const shared = sharedError(g.rows);
        return `
        <section class="mb-6 min-w-0 break-inside-avoid" aria-labelledby="dashUsage-${escapeHtml(g.key)}">
          <h4 id="dashUsage-${escapeHtml(g.key)}" class="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">${providerIconHtml(g.key)}${escapeHtml(g.title)}</h4>
          <p class="text-xs text-gray-500 dark:text-gray-400">${escapeHtml(g.source || "")}</p>
          ${shared ? `<p class="mt-2 rounded-md bg-red-50 px-3 py-2 text-xs break-words text-red-700 dark:bg-red-500/10 dark:text-red-400">${escapeHtml(shared)}</p>` : ""}
          <ul role="list" class="divide-y divide-gray-100 dark:divide-white/5">${(g.rows || []).map((r) => rowHtml(r, now, { hideError: !!shared })).join("")}</ul>
          ${g.key === "gemini" ? capForm((g.rows || []).find((r) => r.key === "gemini_spend"), savingCap) : ""}
        </section>`;
      }).join("")}
    </div>`;
}

function capitalise(text) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
